import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { useForm } from 'react-hook-form';
import logoSGR from '../../assets/SGRLock.png';
import logoSwint from '../../assets/swintLogo.svg';
import AppFooter from '../../components/AppFooter';
import FlowButton from '../../components/FlowButton';
import Input from '../../components/Input';
import LanguageSwitch from '../../components/LanguageSwitch';
import { useAuth } from '../../contexts/authContext';
import { useSteps } from '../../contexts/stepContext';
import { useAppTranslation } from '../../hooks/useAppTranslation';
import api from '../../services/api';
import { errorToast, successToast } from '../../utils/toast';
import {
  BtnCancel,
  BtnSave,
  Container,
  Content,
  ErrMsg,
  FieldInput,
  FieldLabel,
  FooterSlot,
  ForgotLink,
  ModalActions,
  ModalCard,
  ModalSubtitle,
  ModalTitle,
  Overlay,
  RedefinirLink,
} from './styles';

interface LoginProps {
  user: string;
  password: string;
  captcha: string | null | undefined;
}

const Login: React.FC = () => {
  const { register, handleSubmit } = useForm<LoginProps>();
  const { signIn } = useAuth();
  const { handleSetLoading } = useSteps();
  const reCaptchaLoginRef = useRef<ReCAPTCHA>(null);
  const { translationPage } = useAppTranslation();
  const { tp } = translationPage(t => t.pages.login);
  const { tp: tpLogos } = translationPage(t => t.components.logos);
  const { tp: tpForgot } = translationPage(t => t.pages.forgotPassword);

  /* Estado do modal "Esqueci minha senha" — mesmo padrão de modal do
     "Redefinir senha" abaixo, para não parecer que saiu da tela de login. */
  const [showForgotModal, setShowForgotModal] = useState(false);
  const [fpEmail, setFpEmail] = useState('');
  const [fpError, setFpError] = useState('');
  const [fpSent, setFpSent] = useState(false);
  const [fpLoading, setFpLoading] = useState(false);

  /* Estado do modal de redefinição */
  const [showModal, setShowModal] = useState(false);
  const [cpEmail, setCpEmail] = useState('');
  const [cpCurrent, setCpCurrent] = useState('');
  const [cpNew, setCpNew] = useState('');
  const [cpConfirm, setCpConfirm] = useState('');
  const [cpError, setCpError] = useState('');
  const [cpLoading, setCpLoading] = useState(false);

  const closeForgotModal = () => {
    setShowForgotModal(false);
    setFpEmail('');
    setFpError('');
    setFpSent(false);
  };

  const handleForgotPassword = async () => {
    setFpError('');
    if (!fpEmail) {
      setFpError('Informe seu e-mail de acesso.');
      return;
    }

    setFpLoading(true);
    try {
      const { data } = await api.post('/forgot-password', { user: fpEmail });
      if (data.result === 'error') {
        setFpError(data.message);
      } else {
        setFpSent(true);
      }
    } catch {
      setFpError('Erro ao enviar o e-mail. Tente novamente.');
    } finally {
      setFpLoading(false);
    }
  };

  const handleChangePassword = async () => {
    setCpError('');
    if (!cpEmail || !cpCurrent || !cpNew || !cpConfirm) {
      setCpError('Preencha todos os campos.');
      return;
    }
    if (cpNew !== cpConfirm) { setCpError('As senhas não coincidem.'); return; }
    if (cpNew.length < 8) { setCpError('A nova senha deve ter pelo menos 8 caracteres.'); return; }

    setCpLoading(true);
    try {
      const { data } = await api.post('/change-password', {
        user: cpEmail,
        current_password: cpCurrent,
        new_password: cpNew,
        confirm_password: cpConfirm,
      });
      if (data.result === 'success') {
        successToast({ message: data.message });
        setShowModal(false);
        setCpEmail(''); setCpCurrent(''); setCpNew(''); setCpConfirm('');
      } else {
        setCpError(data.message || 'Erro ao alterar a senha.');
      }
    } catch {
      setCpError('Erro ao alterar a senha. Tente novamente.');
    } finally {
      setCpLoading(false);
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('fpc') === '1') {
      sessionStorage.setItem('mustChangePassword', '1');
    }
  }, []);

  const onSubmit = useCallback(
    async (data: LoginProps) => {
      handleSetLoading(true);
      const captcha = await reCaptchaLoginRef.current?.executeAsync();
      reCaptchaLoginRef.current?.reset();
      if (!captcha) { errorToast({ message: tp('captchaRequired') }); return; }
      const loginData = { user: data.user, password: data.password, captcha, action: 'login' };
      const response = await signIn(loginData);
      if (response.error) errorToast({ message: response.message });
      handleSetLoading(false);
    },
    [signIn, handleSetLoading, tp],
  );

  return (
    <Container>
      <LanguageSwitch />
      <img src={logoSGR} alt={tpLogos('sgrLoc')} />
      <Content>
        <img src={logoSwint} alt={tpLogos('swint')} />
        <form onSubmit={handleSubmit(onSubmit)}>
          <Input {...register('user')} type="text" placeholder={tp('userPlaceholder')} label={tp('userLabel')} />
          <Input {...register('password')} type="password" placeholder={tp('passwordPlaceholder')} label={tp('passwordLabel')} />
          <FlowButton type="submit" text={tp('submit')} />
        </form>
        <ForgotLink type="button" onClick={() => setShowForgotModal(true)}>
          {tp('forgotPassword')}
        </ForgotLink>
        <RedefinirLink type="button" onClick={() => setShowModal(true)}>
          Redefinir senha
        </RedefinirLink>
      </Content>

      {/* Fica fora do card: o `backdrop-filter` do vidro cria um bloco de
          contenção e prenderia o selo (position: fixed) dentro do card. */}
      <ReCAPTCHA
        sitekey={process.env.REACT_APP_RECAPTCHA_KEY_SITE || ''}
        size="invisible"
        ref={reCaptchaLoginRef}
      />

      {showForgotModal && (
        <Overlay>
          <ModalCard>
            <ModalTitle>{tpForgot('title')}</ModalTitle>
            {fpSent ? (
              <>
                <ModalSubtitle>{tpForgot('successMessage')}</ModalSubtitle>
                <ModalActions>
                  <BtnSave type="button" onClick={closeForgotModal}>
                    OK
                  </BtnSave>
                </ModalActions>
              </>
            ) : (
              <>
                <ModalSubtitle>
                  Informe seu e-mail de acesso e enviaremos as instruções para
                  redefinir sua senha.
                </ModalSubtitle>
                <FieldLabel>
                  {tpForgot('form.emailLabel')}
                  <FieldInput
                    type="email"
                    value={fpEmail}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setFpEmail(e.target.value)
                    }
                    placeholder={tpForgot('form.emailPlaceholder')}
                    autoComplete="username"
                  />
                </FieldLabel>
                {fpError && <ErrMsg>{fpError}</ErrMsg>}
                <ModalActions>
                  <BtnCancel type="button" onClick={closeForgotModal}>
                    Cancelar
                  </BtnCancel>
                  <BtnSave
                    type="button"
                    onClick={handleForgotPassword}
                    disabled={fpLoading}
                  >
                    {fpLoading ? 'Enviando...' : tpForgot('form.submit')}
                  </BtnSave>
                </ModalActions>
              </>
            )}
          </ModalCard>
        </Overlay>
      )}

      {showModal && (
        <Overlay>
          <ModalCard>
            <ModalTitle>Redefinir senha</ModalTitle>
            <ModalSubtitle>Informe seu e-mail, a senha atual e a nova senha que deseja usar.</ModalSubtitle>
            <FieldLabel>
              E-mail
              <FieldInput type="email" value={cpEmail} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCpEmail(e.target.value)} placeholder="Seu e-mail de acesso" autoComplete="username" />
            </FieldLabel>
            <FieldLabel>
              Senha atual
              <FieldInput type="password" value={cpCurrent} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCpCurrent(e.target.value)} placeholder="Senha atual" autoComplete="current-password" />
            </FieldLabel>
            <FieldLabel>
              Nova senha
              <FieldInput type="password" value={cpNew} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCpNew(e.target.value)} placeholder="Mínimo 8 caracteres" autoComplete="new-password" />
            </FieldLabel>
            <FieldLabel>
              Confirmar nova senha
              <FieldInput type="password" value={cpConfirm} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCpConfirm(e.target.value)} placeholder="Repita a nova senha" autoComplete="new-password" />
            </FieldLabel>
            {cpError && <ErrMsg>{cpError}</ErrMsg>}
            <ModalActions>
              <BtnCancel type="button" onClick={() => { setShowModal(false); setCpError(''); }}>Cancelar</BtnCancel>
              <BtnSave type="button" onClick={handleChangePassword} disabled={cpLoading}>
                {cpLoading ? 'Salvando...' : 'Salvar senha'}
              </BtnSave>
            </ModalActions>
          </ModalCard>
        </Overlay>
      )}

      <FooterSlot>
        <AppFooter />
      </FooterSlot>
    </Container>
  );
};

export default Login;
